import { LoadingOutlined } from "@ant-design/icons";
import { Col, Input, Row, Select } from "antd";
import Checkbox from "antd/lib/checkbox/Checkbox";
import { FC, useState, useEffect } from "react";

import { ICashAllowance, ICountry, IJobGrade, IModal } from "@types";
import {
  ErrorServices,
  useCreateCashAllowanceMutation,
  useFetchAllCompanyJobGradesQuery,
  useFetchCompanyCountriesQuery,
  useUpdateCashAllowanceMutation,

  useFetchAllCompanyJobFunctionsQuery,

} from "@services";
import { ALL_GRADES_VALUE, showSuccessPopup, sortedByName } from "@utils";
import { useFormValidation, useTypedSelector } from "@hooks";
import CustomModal from "@/components/customModel";
import { formatNumberWithCommas } from "@/utils/formatNumberWithCommas";

const { Option } = Select;

interface IAddCashAllowance extends IModal {
  selectedCashAllowance: ICashAllowance | null;
  setSelectedCashAllowance: React.Dispatch<
    React.SetStateAction<ICashAllowance | null>
  >;
}

const INITIAL_CASH_ALLOWANCE: any = {
  allowance_name: null,
  grade_ids: [],
  country_id: null,
  city: null,
  is_all_grade: false,
  is_percentage: false,
  is_basic_pay: false,
  value: null,

  job_function_id: null,
  sub_job_function_ids: [],
};

const AddCashAllowance: FC<IAddCashAllowance> = ({
  isVisible,
  setIsVisible,
  selectedCashAllowance,
  setSelectedCashAllowance,
}) => {
  const { company } = useTypedSelector(state => state.auth?.user!);
  const { id: company_id } = company!;

  const [cashAllowance, setCashAllowance] =
    useState<any>(INITIAL_CASH_ALLOWANCE);

  const [createCashAllowance, { isLoading: isCreating }] =
    useCreateCashAllowanceMutation();

  const [updateCashAllowance, { isLoading: isUpdating }] =
    useUpdateCashAllowanceMutation();

  const [gradeCountries, setGradeCountries] = useState<ICountry[]>([]);

  const validation = useFormValidation(cashAllowance, v => ({
    allowance_name: !v.allowance_name,
    grade_ids:
      !v.is_all_grade &&
      (!v.grade_ids || v.grade_ids.length === 0),
    country: !v.country_id,
    value: !v.value,
    job_function_id: !v.job_function_id,
    sub_job_function_ids:
      !v.sub_job_function_ids ||
      v.sub_job_function_ids.length === 0,
  }));

  const { data: companyJobFunctionsData } =
    useFetchAllCompanyJobFunctionsQuery({
      companyId: company_id,
    });

  const jobFunctions = companyJobFunctionsData?.data || [];

  const subJobFunctions =
    jobFunctions.find(
      (item: any) => item.id === cashAllowance?.job_function_id
    )?.sub_job_functions || [];

  const { data: countriesData, isLoading: isFetchingCountries } =
    useFetchCompanyCountriesQuery({
      company_id,
    });

  const { data: countries } = countriesData || {};

  const { data: jobGradesData, isLoading: isFetchingGrades } =
    useFetchAllCompanyJobGradesQuery({ company_id });

  const { data: jobGrades } = jobGradesData || {};

  useEffect(() => {
    if (selectedCashAllowance) {
      const { country } = selectedCashAllowance;

      const cashAllowanceData: any = {
        ...selectedCashAllowance,

        value:
          String(selectedCashAllowance?.value)?.replace(/,/g, "") ||
          selectedCashAllowance?.value,

        country_id: country?.id || null,

        grade_ids: selectedCashAllowance?.grade_obj?.id
          ? [selectedCashAllowance.grade_obj.id]
          : selectedCashAllowance?.grades?.length
            ? selectedCashAllowance.grades.map((g: any) => g.id)
            : [],

        job_function_id:
          selectedCashAllowance?.job_function?.[0]?.id ||
          selectedCashAllowance?.job_function?.id ||
          null,

        sub_job_function_ids:
          selectedCashAllowance?.job_sub_function?.map(
            (item: any) => item.id
          ) ||
          selectedCashAllowance?.sub_job_function_ids ||
          [],
      };

      delete cashAllowanceData.id;
      delete cashAllowanceData.country;
      delete cashAllowanceData.grade;
      delete cashAllowanceData.grade_obj;
      delete cashAllowanceData.grades;

      if (country) {
        setGradeCountries([country]);
      }

      setCashAllowance(cashAllowanceData);
    }

    return () => {
      setSelectedCashAllowance(null);
      setCashAllowance(INITIAL_CASH_ALLOWANCE);
    };
  }, [selectedCashAllowance, setSelectedCashAllowance]);

  const handleChange = (event: any) => {
    const { name, value, checked } = event.target;

    const cleanedValue = value ? value.replace(/,/g, "") : "";

    setCashAllowance((prev: any) => ({
      ...prev,
      [name]: name?.includes("is_") ? checked : cleanedValue,
    }));

    if (name?.includes("is_all_grade") && checked) {
      setGradeCountries(countries);
    }
  };

  const handleSubmit = async () => {
    const { city, value } = cashAllowance;

    const body = {
      ...cashAllowance,
      city: city || null,
      value: +value! || null,

      grade_ids: selectedCashAllowance
        ? cashAllowance?.grade_ids?.slice(0, 1) || []
        : cashAllowance?.is_all_grade
          ? []
          : cashAllowance?.grade_ids || [],
    };

    body?.id && delete body?.id;
    body?.country && delete body?.country;
    body?.grade && delete body?.grade;
    body?.grade_obj && delete body?.grade_obj;
    body?.grades && delete body?.grades;

    try {
      if (selectedCashAllowance) {
        await editCashAllowance(body);
      } else {
        await addCashAllowance(body);
      }

      setIsVisible(false);

      showSuccessPopup({
        title: selectedCashAllowance
          ? "Cash Allowance Updated"
          : "New Cash Allowance Created",
        desc: `You have successfully ${selectedCashAllowance ? "updated the" : "created new"
          } cash allowance.`,
        role: "client",
      });
    } catch (error) {
      ErrorServices(error);
    }
  };

  const addCashAllowance = async (body: ICashAllowance) =>
    await createCashAllowance({
      company_id,
      body,
    }).unwrap();

  const editCashAllowance = async (body: ICashAllowance) =>
    updateCashAllowance({
      company_id,
      id: selectedCashAllowance?.id,
      body,
    }).unwrap();

  return (
    <CustomModal
      width="w-7/12"
      footer={
        <>
          <button
            className="bg-revampOrange text-white font-medium text-lg py-2 px-14 rounded-md"
            onClick={() => setIsVisible(false)}
          >
            Cancel
          </button>

          <button
            className={`bg-revampBlue text-white font-medium text-lg py-2 px-14 rounded-md ${!validation.isValid && "opacity-50"
              }`}
            disabled={!validation.isValid || isCreating || isUpdating}
            onClick={handleSubmit}
          >
            {isCreating || isUpdating ? (
              <LoadingOutlined className="spinner" />
            ) : selectedCashAllowance ? (
              "Update"
            ) : (
              "Create"
            )}
          </button>
        </>
      }
      title={`${selectedCashAllowance ? "Update" : "Create"} Cash Allowance`}
      isVisible={isVisible}
    >
      <>
        <Row justify="space-between" className="modal__row">
          <Col span={24}>
            <label className="font-medium text-revampBlue text-lg leading-10 mt-4 ">
              Name
            </label>

            <Input
              className="borderless-input w-full shadow-sm my-2 font-semibold text-sm outline-none px-3 py-3 rounded-md"
              name="allowance_name"
              value={cashAllowance?.allowance_name || ""}
              onBlur={() => validation.touch("allowance_name")}
              onChange={handleChange}
              size="large"
              placeholder="Enter allowance name here..."
            />
          </Col>
        </Row>

        <Row justify="space-between" className="modal__row">
          <Col span={11}>
            <label className="font-medium text-revampBlue text-lg leading-10 mt-4">
              Job Function
            </label>

            <Select
              className="custom-select"
              size="large"
              placeholder="Select job function..."
              value={cashAllowance?.job_function_id || undefined}
              onChange={job_function_id =>
                setCashAllowance((prev: any) => ({
                  ...prev,
                  job_function_id,
                  sub_job_function_ids: [],
                }))
              }
            >
              {jobFunctions?.map((item: any) => (
                <Option key={item.id} value={item.id}>
                  {item.name}
                </Option>
              ))}
            </Select>
          </Col>

          <Col span={11}>
            <label className="font-medium text-revampBlue text-lg leading-10 mt-4">
              Sub Job Function
            </label>

            <Select
              mode="multiple"
              maxTagCount="responsive"
              maxTagPlaceholder={(omittedValues) => `+${omittedValues.length} more`}
              className="custom-select"
              size="large"
              placeholder="Select sub job functions..."
              value={cashAllowance?.sub_job_function_ids}
              disabled={!cashAllowance?.job_function_id}
              onChange={(sub_job_function_ids: number[]) =>
                setCashAllowance(prev => ({
                  ...prev,
                  sub_job_function_ids,
                }))
              }
            >
              {subJobFunctions.map((item: any) => (
                <Option key={item.id} value={item.id}>
                  {item.name}
                </Option>
              ))}
            </Select>
          </Col>
        </Row>

        <Row justify="space-between" className="modal__row">
          <Col span={11}>
            <label className="font-medium text-revampBlue text-lg leading-10 mt-4 ">
              Grades
            </label>

            <Select
              className="custom-select"
              loading={isFetchingGrades}
              mode={selectedCashAllowance ? undefined : "multiple"}
              showArrow
              size="large"
              placeholder="Select grades from here..."
              value={
                selectedCashAllowance
                  ? cashAllowance?.grade_ids?.[0]
                  : cashAllowance?.grade_ids || []
              }
              onBlur={() => validation.touch("grade_ids")}
              optionLabelProp="label"
              onChange={(value: number | number[]) => {
                const selectedGradeIds = Array.isArray(value)
                  ? value
                  : [value];

                if (
                  !selectedCashAllowance &&
                  selectedGradeIds.includes(
                    ALL_GRADES_VALUE as any
                  )
                ) {
                  const allGradeIds =
                    jobGrades?.map(
                      (jg: IJobGrade) => jg.id
                    ) || [];

                  const allCountries =
                    jobGrades
                      ?.flatMap(jg => jg.countries || [])
                      ?.filter(
                        (c, idx, self) =>
                          c &&
                          idx ===
                          self.findIndex(
                            sc => sc.id === c.id
                          )
                      ) || [];

                  setGradeCountries(allCountries);

                  setCashAllowance((prev: any) => ({
                    ...prev,
                    grade_ids: allGradeIds,
                    country_id: null,
                  }));

                  return;
                }

                const selectedJobGrades =
                  jobGrades?.filter((jg: IJobGrade) =>
                    selectedGradeIds.includes(jg.id)
                  ) || [];

                const mergedCountries =
                  selectedJobGrades.flatMap(
                    jg =>
                      jg?.countries?.length
                        ? jg.countries
                        : countries || []
                  ) || [];

                const uniqueCountries = mergedCountries.filter(
                  (country, index, self) =>
                    country &&
                    index ===
                    self.findIndex(c => c.id === country.id)
                );

                setGradeCountries(uniqueCountries);

                setCashAllowance((prev: any) => ({
                  ...prev,
                  grade_ids: selectedCashAllowance
                    ? [selectedGradeIds[0]]
                    : selectedGradeIds,
                  country_id: null,
                }));
              }}

              maxTagCount="responsive"
              maxTagPlaceholder={(omittedValues) => `+${omittedValues.length} more`}
            >
              {!selectedCashAllowance && (
                <Option
                  key={ALL_GRADES_VALUE}
                  value={ALL_GRADES_VALUE}
                >
                  All Grades
                </Option>
              )}

              {jobGrades?.map((jobGrade: IJobGrade) => (
                <Option
                  label={`${jobGrade.grade}`}
                  key={jobGrade.id}
                  value={jobGrade.id}
                >
                  {jobGrade.grade}
                </Option>
              ))}
            </Select>
          </Col>

          <Col span={11}>
            <label className="font-medium text-revampBlue text-lg leading-10 mt-4 ">
              Country
            </label>

            <Select
              size="large"
              className="custom-select"
              showArrow
              placeholder="Select country from here..."
              showSearch={false}
              value={
                isFetchingCountries && selectedCashAllowance
                  ? undefined
                  : cashAllowance?.country_id || undefined
              }
              onBlur={() => validation.touch("country")}
              onChange={country_id =>
                setCashAllowance((prev: any) => ({
                  ...prev,
                  country_id,
                }))
              }

            >
              {sortedByName(gradeCountries)?.map(
                (country: ICountry) => (
                  <Option key={country?.id} value={country?.id}>
                    {country?.name}
                  </Option>
                )
              )}
            </Select>
          </Col>
        </Row>

        <Row justify="space-between" className="modal__row">
          <Col span={11}>
            <label className="font-medium text-revampBlue text-lg leading-10 mt-4 ">
              City
            </label>

            <Input
              className="borderless-input w-full shadow-sm my-2 font-semibold text-sm outline-none px-3 py-3 rounded-md"
              name="city"
              value={cashAllowance?.city || ""}
              onChange={handleChange}
              size="large"
              placeholder="Enter city here..."
            />
          </Col>

          <Col span={11}>
            <label className="font-medium text-revampBlue text-lg leading-10 mt-4 ">
              Value
            </label>

            <Input
              className="borderless-input w-full shadow-sm my-2 font-semibold text-sm outline-none px-3 py-3 rounded-md"
              name="value"
              value={formatNumberWithCommas(
                cashAllowance?.value || ""
              )}
              onBlur={() => validation.touch("value")}
              onChange={handleChange}
              type="text"
              size="large"
              placeholder="Enter value here..."
            />
          </Col>
        </Row>

        <Row className="modal__row">
          <Checkbox
            checked={cashAllowance?.is_percentage}
            name="is_percentage"
            onChange={handleChange}
          >
            <span className="font-medium text-lg text-themeInputTitleColor">
              Percentage
            </span>
          </Checkbox>
        </Row>
      </>
    </CustomModal>
  );
};

export default AddCashAllowance;